import React, { useEffect, useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Stack,
} from "@mui/material";
import {
  Dashboard,
  AttachMoney,
  Inventory,
  LocalShipping,
  CheckCircle,
  Memory,
  Code,
  Payment as PaymentIcon,
  Close,
} from "@mui/icons-material";
import EmbedCodeModal from "../../modals/EmbedCodeModal";
import { useDispatch } from "react-redux";
import { getCarrierByBusinessName } from "../../redux/action/carrier";
import { unwrapResult } from "@reduxjs/toolkit";

interface SidebarProps {
  sidebarOpen: boolean;
  setActiveComponent: (component: string) => void;
  toggleSidebar: () => void;
}

const SideBar: React.FC<SidebarProps> = ({
  sidebarOpen,
  setActiveComponent,
  toggleSidebar,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const businessName = localStorage.getItem("businessName");
  const [carrierName, setCarrierName] = useState(businessName);
  const [activeComponent, setActiveComponentState] = useState<string>("");
  const [embedModalOpen, setEmbedModalOpen] = useState(false);
  const [carrier, setCarrier] = useState<any>(null);

  const dispatch = useDispatch();

  // Function to check if recent booking payment exists within given days (default 30)
  function hasRecentBookingPayment(days = 30): boolean {
    // const payments = carrier?.subscription_payments || [];
    // if (payments.length === 0) return false;

    // const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    // return payments.some(
    //   (payment: any) =>
    //     payment.is_booking_tool_included === true &&
    //     new Date(payment.checkout_time) > cutoff
    // );
    return true;
  }

  // Sidebar items that are always visible
  const alwaysVisibleItems = [
    // { text: "Billing", icon: <PaymentIcon />, component: "Billing" },
    {
      text: "Carrier Booking Tool",
      icon: <Dashboard />,
      component: "CarrierInfo",
    },
    {
      text: "SMS Inventory Tool",
      icon: <Inventory />,
      component: "SMS Inventory Tool",
    },
  ];

  // Sidebar items visible only if recent booking payment exists
  const conditionalItems = [
    { text: "Rates", icon: <AttachMoney />, component: "Rates" },
    { text: "Box Rates", icon: <Inventory />, component: "BoxRates" },
    {
      text: "Long Distance",
      icon: <LocalShipping />,
      component: "LongDistance",
    },
    { text: "Insurance", icon: <CheckCircle />, component: "Insurance" },
    { text: "Stats", icon: <AttachMoney />, component: "Stats" },
    { text: "AI Bot", icon: <Memory />, component: "AIBot" },
  ];

  const getCarrier = () => {
    dispatch<any>(getCarrierByBusinessName(businessName || ""))
      .then(unwrapResult)
      .then((res: any) => {
        setCarrier(res);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  useEffect(() => {
    if (businessName && carrier === null) {
      getCarrier();
    }
  }, [businessName]);

  const bookingPaymentExists = hasRecentBookingPayment();
  const filteredConditionalItems = bookingPaymentExists ? conditionalItems : [];

  const handleItemClick = (component: string) => {
    setActiveComponentState(component);
    setActiveComponent(component);
    if (isMobile) {
      toggleSidebar();
    }
  };

  return (
    <>
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={sidebarOpen}
        onClose={toggleSidebar}
        sx={{
          width: sidebarOpen ? 240 : 0,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: sidebarOpen ? 240 : 0,
            boxSizing: "border-box",
            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: theme.palette.primary.contrastText,
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            ...(isMobile && {
              position: "fixed",
              zIndex: theme.zIndex.drawer + 2,
            }),
          },
        }}
      >
        {isMobile && (
          <IconButton
            onClick={toggleSidebar}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: theme.palette.primary.contrastText,
              zIndex: theme.zIndex.drawer + 3,
            }}
          >
            <Close />
          </IconButton>
        )}
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            borderRadius: 1,
            mx: 2,
            my: 1,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            borderLeft: `4px solid ${theme.palette.secondary.main}`,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: theme.palette.primary.contrastText,
              opacity: 0.8,
              mb: 0.5,
              textTransform: "uppercase",
              letterSpacing: 1,
              fontSize: "0.75rem",
            }}
          >
            Carrier
          </Typography>
          <Tooltip title={carrierName || ""} arrow placement="bottom">
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.contrastText,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
                lineHeight: 1.2,
              }}
            >
              {carrierName}
            </Typography>
          </Tooltip>
        </Box>
        <Divider />
        <List>
          {[...alwaysVisibleItems, ...filteredConditionalItems].map(
            ({ text, icon, component }) => (
              <ListItem
                key={component}
                component="li"
                onClick={() => handleItemClick(component)}
                sx={{
                  backgroundColor:
                    activeComponent === component
                      ? theme.palette.action.selected
                      : "transparent",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <ListItemButton>
                  <ListItemIcon
                    sx={{ color: theme.palette.primary.contrastText }}
                  >
                    {icon}
                  </ListItemIcon>
                  <ListItemText primary={text} />
                </ListItemButton>
              </ListItem>
            )
          )}
          {hasRecentBookingPayment() && (
            <ListItem
              component="li"
              onClick={() => setEmbedModalOpen(true)}
              sx={{
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <ListItemButton>
                <ListItemIcon
                  sx={{ color: theme.palette.primary.contrastText }}
                >
                  <Code />
                </ListItemIcon>
                <ListItemText primary="Embed Code" />
              </ListItemButton>
            </ListItem>
          )}
        </List>
        <Divider />
      </Drawer>
      <EmbedCodeModal
        canonicalId={carrier?.canonical_id}
        open={embedModalOpen}
        onClose={() => setEmbedModalOpen(false)}
      />
    </>
  );
};

export default SideBar;
